/**
 * Cache Manager - Handles data caching for improved performance
 * Caches data in localStorage with expiration and user-specific scoping
 */

import { getSession } from './authUtils';
import logger from './logger';

class CacheManager {
  constructor() {
    this.cachePrefix = 'visibeen_cache_';
    this.defaultTTL = 30 * 60 * 1000; // 30 minutes default
  }

  /**
   * Determine if the current browser context is impersonating a user
   * via admin access (stored in sessionStorage/localStorage).
   * Returns a scoped identifier when impersonating, otherwise null.
   */
  getImpersonationScope() {
    if (typeof window === 'undefined') return null;

    // Try sessionStorage admin access payload first
    try {
      const adminAccessRaw = window.sessionStorage?.getItem('adminAccessData');
      if (adminAccessRaw) {
        const adminAccess = JSON.parse(adminAccessRaw);
        if (adminAccess?.isAdminAccess && (adminAccess.targetUserId || adminAccess.targetUserEmail)) {
          return `admin-view:${adminAccess.targetUserId || adminAccess.targetUserEmail}`;
        }
      }
    } catch (error) {
      logger.warn('[CacheManager] Failed reading adminAccessData scope:', error);
    }

    // Fallback to the derived user context
    try {
      const userContextRaw = window.sessionStorage?.getItem('currentUserContext');
      if (userContextRaw) {
        const userContext = JSON.parse(userContextRaw);
        if (userContext?.isAdminView && (userContext.userId || userContext.userEmail)) {
          return `admin-view:${userContext.userId || userContext.userEmail}`;
        }
      }
    } catch (error) {
      logger.warn('[CacheManager] Failed reading currentUserContext scope:', error);
    }

    // Fallback to the lightweight flag stored with user id only
    try {
      const adminViewingId = window.sessionStorage?.getItem('adminViewingUserId');
      if (adminViewingId) {
        return `admin-view:${adminViewingId}`;
      }
    } catch (error) {
      logger.warn('[CacheManager] Failed reading adminViewingUserId scope:', error);
    }

    return null;
  }

  /**
   * Get current user identifier for cache scoping
   */
  getCurrentUserId() {
    const impersonationScope = this.getImpersonationScope();
    if (impersonationScope) {
      return impersonationScope;
    }

    const session = getSession();
    return session?.id || session?.user?.id || session?.email || 'anonymous';
  }

  /**
   * Generate cache key with user scope
   */
  getCacheKey(key) {
    const userId = this.getCurrentUserId();
    return `${this.cachePrefix}${userId}_${key}`;
  }

  /**
   * Set data in cache with expiration
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} ttl - Time to live in milliseconds (default: 30 minutes)
   */
  set(key, data, ttl = this.defaultTTL) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cacheData = {
        data,
        timestamp: Date.now(),
        expiresAt: Date.now() + ttl,
        userId: this.getCurrentUserId()
      };
      
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      logger.log(`[CacheManager] ✅ Cached: ${key} (TTL: ${ttl / 1000}s)`);
      return true;
    } catch (error) {
      logger.error('[CacheManager] Error setting cache:', error);
      return false;
    }
  }

  /**
   * Get data from cache if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/not found
   */
  get(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) {
        logger.log(`[CacheManager] ❌ Cache miss: ${key}`);
        return null;
      }

      const cacheData = JSON.parse(cached);
      const now = Date.now();

      // Check if expired
      if (now > cacheData.expiresAt) {
        logger.log(`[CacheManager] ⏰ Cache expired: ${key}`);
        this.remove(key);
        return null;
      }

      // Check if user matches
      if (cacheData.userId !== this.getCurrentUserId()) {
        logger.log(`[CacheManager] 👤 User mismatch: ${key}`);
        this.remove(key);
        return null;
      }

      const age = Math.round((now - cacheData.timestamp) / 1000);
      logger.log(`[CacheManager] ✅ Cache hit: ${key} (age: ${age}s)`);
      return cacheData.data;
    } catch (error) {
      logger.error('[CacheManager] Error getting cache:', error);
      return null;
    }
  }

  /**
   * Remove specific cache entry
   */
  remove(key) {
    try {
      const cacheKey = this.getCacheKey(key);
      localStorage.removeItem(cacheKey);
      logger.log(`[CacheManager] 🗑️ Removed cache: ${key}`);
      return true;
    } catch (error) {
      logger.error('[CacheManager] Error removing cache:', error);
      return false;
    }
  }

  /**
   * Clear all cache for current user
   */
  clearUserCache() {
    try {
      const userId = this.getCurrentUserId();
      const keys = Object.keys(localStorage);
      let cleared = 0;

      keys.forEach(key => {
        if (key.startsWith(`${this.cachePrefix}${userId}_`)) {
          localStorage.removeItem(key);
          cleared++;
        }
      });

      logger.log(`[CacheManager] 🧹 Cleared ${cleared} cache entries for user`);
      return cleared;
    } catch (error) {
      logger.error('[CacheManager] Error clearing user cache:', error);
      return 0;
    }
  }

  /**
   * Clear all expired cache entries
   */
  clearExpired() {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      let cleared = 0;

      keys.forEach(key => {
        if (key.startsWith(this.cachePrefix)) {
          try {
            const cached = localStorage.getItem(key);
            const cacheData = JSON.parse(cached);
            
            if (now > cacheData.expiresAt) {
              localStorage.removeItem(key);
              cleared++;
            }
          } catch (e) {
            // Invalid cache entry, remove it
            localStorage.removeItem(key);
            cleared++;
          }
        }
      });

      if (cleared > 0) {
        logger.log(`[CacheManager] 🧹 Cleared ${cleared} expired cache entries`);
      }
      return cleared;
    } catch (error) {
      logger.error('[CacheManager] Error clearing expired cache:', error);
      return 0;
    }
  }

  /**
   * Get or fetch data with caching
   * @param {string} key - Cache key
   * @param {Function} fetchFn - Function to fetch data if not cached
   * @param {number} ttl - Time to live in milliseconds
   */
  async getOrFetch(key, fetchFn, ttl = this.defaultTTL) {
    // Try to get from cache first
    const cached = this.get(key);
    if (cached !== null) {
      return { data: cached, fromCache: true };
    }

    // Fetch fresh data
    logger.log(`[CacheManager] 🔄 Fetching fresh data: ${key}`);
    try {
      const data = await fetchFn();
      this.set(key, data, ttl);
      return { data, fromCache: false };
    } catch (error) {
      logger.error(`[CacheManager] Error fetching data for ${key}:`, error);
      throw error;
    }
  }

  /**
   * Check if cache exists and is valid
   */
  has(key) {
    const cached = this.get(key);
    return cached !== null;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    try {
      const userId = this.getCurrentUserId();
      const keys = Object.keys(localStorage);
      const userCacheKeys = keys.filter(key => key.startsWith(`${this.cachePrefix}${userId}_`));
      
      let totalSize = 0;
      let expiredCount = 0;
      const now = Date.now();

      userCacheKeys.forEach(key => {
        const cached = localStorage.getItem(key);
        totalSize += cached.length;
        
        try {
          const cacheData = JSON.parse(cached);
          if (now > cacheData.expiresAt) {
            expiredCount++;
          }
        } catch (e) {
          expiredCount++;
        }
      });

      return {
        totalEntries: userCacheKeys.length,
        expiredEntries: expiredCount,
        totalSize: `${(totalSize / 1024).toFixed(2)} KB`,
        userId
      };
    } catch (error) {
      logger.error('[CacheManager] Error getting stats:', error);
      return null;
    }
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

// Clear expired cache on initialization
cacheManager.clearExpired();

// Clear expired cache every 5 minutes
setInterval(() => {
  cacheManager.clearExpired();
}, 5 * 60 * 1000);

export default cacheManager;
